import React, { useState } from 'react';
import { Box, Drawer, Card, CardContent, CardHeader, Typography, CircularProgress } from '@mui/material';
import api from '../../../../services/api';
import FormAddEditToolbar from '../../../../components/global/AddEditBar';
import { useNotifier } from 'muinotify';
import ExpenseDetailsForm from '../ExpenseDetailsForm';
import useSWR, { mutate } from 'swr';
import { confirmAlert } from 'react-confirm-alert';
import {
  redistributeFixedTotal,
  getDetailsTotal,
  PREDICTION_TOTAL_MODES,
} from '../../../../components/utils/expenseDetailsUtils';

const URL = `expense-detail`;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const ExpenseDetailsEdit = ({ id, onClose }) => {
  const { notifier } = useNotifier();
  const [state, setState] = useState({ expense_details: [] });
  const [errors, setErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(true);
  const [expenseTotalAmount, setExpenseTotalAmount] = useState(null);
  const [isPrediction, setIsPrediction] = useState(false);
  const [totalMode, setTotalMode] = useState(PREDICTION_TOTAL_MODES.FIXED);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [lockedDetailIds, setLockedDetailIds] = useState(() => new Set());

  const toggleDetailLock = (detailId) => {
    setLockedDetailIds((prev) => {
      const next = new Set(prev);
      if (next.has(detailId)) {
        next.delete(detailId);
      } else {
        next.add(detailId);
      }
      return next;
    });
  };

  function updateExpenseDetail(detailId, fieldName, value, options = {}) {
    if (fieldName !== 'exp_d_amount') {
      setState({
        ...state,
        expense_details: state?.expense_details.map((detail) =>
          detail.id === detailId ? { ...detail, [fieldName]: value } : detail
        ),
      });
      return;
    }

    const shouldRedistribute =
      isPrediction &&
      totalMode === PREDICTION_TOTAL_MODES.FIXED &&
      expenseTotalAmount != null &&
      options.redistribute;

    if (shouldRedistribute) {
      const parsed = parseFloat(value);
      if (isNaN(parsed)) return;

      setState((prev) => ({
        ...prev,
        expense_details: redistributeFixedTotal(
          prev.expense_details,
          detailId,
          parsed,
          expenseTotalAmount,
          Array.from(lockedDetailIds)
        ),
      }));
      return;
    }

    setState({
      ...state,
      expense_details: state?.expense_details.map((detail) =>
        detail.id === detailId ? { ...detail, [fieldName]: value } : detail
      ),
    });
  }

  function handleAmountBlur(detailId, value) {
    if (
      isPrediction &&
      totalMode === PREDICTION_TOTAL_MODES.FIXED &&
      expenseTotalAmount != null
    ) {
      updateExpenseDetail(detailId, 'exp_d_amount', value, { redistribute: true });
    }
  }

  const clientKey = `/${URL}/${id}`;

  useSWR(clientKey, {
    revalidateOnFocus: false,
    revalidateOnMount: true,
    onSuccess: (expenseDetails) => {
      if (expenseDetails) {
        setState({
          expense_details: expenseDetails.map((detail) => ({
            ...detail,
            original_exp_d_amount: detail.exp_d_amount,
          })),
        });
      }
    },
  });

  const calculatedTotal = getDetailsTotal(state?.expense_details);

  useSWR(`/expense/${id}`, {
    revalidateOnFocus: false,
    revalidateOnMount: true,
    onSuccess: (expense) => {
      if (expense) {
        setExpenseTotalAmount(expense.exp_amount);
        setIsPrediction(!!expense.exp_pred);
        setIsUpdating(false);
      }
    },
  });

  const closeDrawer = () => {
    setDrawerOpen(onClose);
  };

  const handleSave = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setTimeout(() => {
      const form = document.getElementById(`${URL}-form`);
      if (form) {
        form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }, 0);
  };

  const saveDetails = async (updateTotal) => {
    const editedDetails = (state?.expense_details || []).filter(
      (detail) =>
        parseFloat(detail.exp_d_amount) !== parseFloat(detail.original_exp_d_amount)
    );

    if (editedDetails.length === 0 && !updateTotal) {
      notifier('Δεν υπάρχουν αλλαγές προς αποθήκευση.', { variant: 'warning' });
      return;
    }

    setIsUpdating(true);

    try {
      await Promise.all(
        editedDetails.map((detail) =>
          api.patch(`/${URL}/${detail.id}`, {
            ...detail,
            exp_d_amount: parseFloat(detail.exp_d_amount),
          })
        )
      );

      if (updateTotal) {
        await api.patch(`/expense/${id}/amount`, { exp_amount: calculatedTotal });
      }

      await mutate(`/expense/${id}`);
      await mutate(clientKey);
      await mutate('/expense');

      notifier(
        updateTotal
          ? 'Η κατανομή και το συνολικό ποσό της πρόβλεψης ενημερώθηκαν.'
          : 'Η κατανομή ενημερώθηκε.',
        { variant: 'success' }
      );
      closeDrawer();
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || 'Άγνωστο σφάλμα';
      notifier(`Σφάλμα αποθήκευσης: ${message}`, { variant: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const totalMismatch =
      expenseTotalAmount !== null &&
      calculatedTotal.toFixed(2) !== expenseTotalAmount.toFixed(2);

    if (!isPrediction && totalMismatch) {
      notifier(
        'Το συνολικό ποσό δεν ταιριάζει με το ποσό του τιμολογίου. Προσαρμόστε τα ποσά ανά επεισόδιο.',
        { variant: 'error' }
      );
      return;
    }

    if (isPrediction && totalMode === PREDICTION_TOTAL_MODES.FIXED) {
      if (totalMismatch) {
        notifier(
          `Το άθροισμα (${formatCurrency(calculatedTotal)}) υπερβαίνει το σύνολο της πρόβλεψης (${formatCurrency(expenseTotalAmount)}). Μειώστε κάποιο ποσό.`,
          { variant: 'error' }
        );
        return;
      }
      await saveDetails(false);
      return;
    }

    if (isPrediction && totalMode === PREDICTION_TOTAL_MODES.VARIABLE && totalMismatch) {
      confirmAlert({
        title: 'Επιβεβαίωση αλλαγής συνόλου',
        message: `Το σύνολο άλλαξε από ${formatCurrency(expenseTotalAmount)} σε ${formatCurrency(calculatedTotal)}. Να ενημερωθεί η πρόβλεψη;`,
        buttons: [
          {
            label: 'Ναι',
            onClick: () => saveDetails(true),
          },
          {
            label: 'Όχι',
          },
        ],
      });
      return;
    }

    await saveDetails(false);
  };

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      PaperProps={{
        style: {
          width: '100%',
          height: '100vh',
          maxWidth: 600,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 800,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box display="flex" justifyContent="center" alignItems="center" width="100%">
          <CardHeader
            title={
              <Typography variant="h3" gutterBottom>
                Επεξεργασία Στοιχείων Εξόδων
              </Typography>
            }
          />
        </Box>

        <CardContent
          sx={{
            display: 'flex',
            flexGrow: 1,
            minHeight: 0,
            overflowY: 'auto',
            margin: 'initial',
          }}
        >
          {isUpdating && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                zIndex: 1000,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {!isUpdating && expenseTotalAmount !== null && calculatedTotal > 0 && (
            <form id={`${URL}-form`} onSubmit={handleSubmit} style={{ width: '100%' }}>
              <ExpenseDetailsForm
                state={state}
                errors={errors}
                updateField={updateExpenseDetail}
                onAmountBlur={handleAmountBlur}
                expenseTotalAmount={expenseTotalAmount}
                setErrors={setErrors}
                isPrediction={isPrediction}
                totalMode={totalMode}
                onTotalModeChange={setTotalMode}
                lockedDetailIds={lockedDetailIds}
                onToggleLock={toggleDetailLock}
              />
            </form>
          )}
        </CardContent>

        <Box>
          <FormAddEditToolbar handleSave={handleSave} handleCancel={onClose} />
        </Box>
      </Card>
    </Drawer>
  );
};

export default ExpenseDetailsEdit;
